import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Send,
  Bot,
  User,
  X,
  Minimize2,
  Expand,
  RotateCcw,
  Download,
  Copy,
  BotMessageSquare,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import { ChatMessage } from '@/types/ai';
import { useGetAiChatbotHistoryQuery, usePostAiChatbotMutation } from '@/services';
import { ChartRenderer } from './chart-renderer';

interface AIChatWidgetProps {
  className?: string;
}

// Utility function for date parsing
function parseIsoDate(date: any): Date {
  if (!date || typeof date !== 'string') return new Date();
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

// Message bubble component
const MessageBubble = memo(({ message, onCopy }: { message: ChatMessage; onCopy: (text: string) => void }) => {
  const isUser = message.type === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/50 dark:to-purple-900/50 flex items-center justify-center border border-blue-200/50 dark:border-blue-700/50">
          <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
      )}

      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
            : 'bg-white/70 dark:bg-gray-800/70 border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm'
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>

        {/* Chart attachments */}
        {message.attachments?.map((attachment, index) => (
          <div key={index} className="mt-4">
            {attachment.type === 'chart' && (
              <div className="border border-gray-200/50 dark:border-gray-700/50 rounded-xl p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">AI Visualization</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                      onClick={() => onCopy(JSON.stringify(attachment.data))}
                      title="Copy data"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                      title="Download"
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <ChartRenderer attachment={attachment} />
              </div>
            )}
          </div>
        ))}

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs opacity-60">{message.timestamp.toLocaleTimeString()}</p>
          {!isUser && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 opacity-40 hover:opacity-100 transition-opacity"
              onClick={() => onCopy(message.content)}
              title="Copy message"
            >
              <Copy className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center border border-gray-300/50 dark:border-gray-600/50">
          <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </div>
      )}
    </div>
  );
});

MessageBubble.displayName = 'MessageBubble';

// Loading indicator component
const TypingIndicator = memo(() => (
  <div className="flex gap-3 justify-start">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/50 dark:to-purple-900/50 flex items-center justify-center border border-blue-200/50 dark:border-blue-700/50">
      <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-pulse" />
    </div>
    <div className="bg-white/70 dark:bg-gray-800/70 rounded-2xl px-4 py-3 border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm">
      <div className="flex space-x-1">
        {[0, 150, 300].map((delay, i) => (
          <div
            key={i}
            className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </div>
    </div>
  </div>
));

TypingIndicator.displayName = 'TypingIndicator';

// Collapsible suggestion panel
const SuggestionPanel = memo(
  ({
    suggestions,
    onSuggestionClick,
    isCollapsed,
    onToggleCollapse,
  }: {
    suggestions: string[];
    onSuggestionClick: (suggestion: string) => void;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
  }) => (
    <div className="border-t border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-blue-50/30 to-purple-50/30 dark:from-blue-950/20 dark:to-purple-950/20 backdrop-blur-sm">
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-between h-10 px-4 text-xs text-gray-600 dark:text-gray-300 hover:bg-blue-50/50 dark:hover:bg-blue-950/30"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-3 w-3" />
          Quick Actions
        </div>
        {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
      </Button>

      {!isCollapsed && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className="text-xs h-9 px-3 justify-start bg-white/50 dark:bg-gray-800/50 hover:bg-blue-50 dark:hover:bg-blue-950/50 border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm transition-all duration-200"
                onClick={() => onSuggestionClick(suggestion)}
              >
                <span className="truncate">{suggestion}</span>
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  ),
);

SuggestionPanel.displayName = 'SuggestionPanel';

export function AIChatWidget({ className }: AIChatWidgetProps) {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullExpanded, setIsFullExpanded] = useState(false);
  const [isSuggestionsCollapsed, setIsSuggestionsCollapsed] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // API hooks
  const [sendMessageApi] = usePostAiChatbotMutation();
  const { data: historyData } = useGetAiChatbotHistoryQuery({ limit: '20', offset: '0' });

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Memoized values
  const defaultSuggestionQueries = useMemo(
    () => [
      'Show temperature trends',
      'Sensor distribution chart',
      'Check for anomalies',
      'Energy consumption report',
      'Device health summary',
      'Optimization recommendations',
    ],
    [],
  );

  const [suggestionQueries, setSuggestionQueries] = useState(defaultSuggestionQueries);

  const defaultAssistantMessage: ChatMessage = useMemo(
    () => ({
      type: 'assistant',
      content:
        "👋 Hi! I'm your AI assistant. I can help you analyze your IoT data, check device status, and provide insights. Use the quick actions below or ask me anything!",
      timestamp: new Date(),
    }),
    [],
  );

  const [messages, setMessages] = useState<ChatMessage[]>([defaultAssistantMessage]);

  // Effects
  useEffect(() => {
    const parsedMessages: ChatMessage[] = (historyData?.data.items ?? []).map((item) => ({
      id: item.id ?? undefined,
      type: item.type,
      content: item.content,
      timestamp: parseIsoDate(item.timestamp),
      attachments: item.attachments,
    }));

    if (parsedMessages.length) {
      setMessages(parsedMessages);
      const lastMessage = parsedMessages[parsedMessages.length - 1];
      if (lastMessage.id) {
        setCurrentSessionId(lastMessage.id);
      }
    } else {
      setMessages([defaultAssistantMessage]);
      setCurrentSessionId(null);
    }
  }, [historyData, defaultAssistantMessage]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isMinimized]);

  // Update suggestions based on last assistant message
  const lastAssistantQuestions = useMemo(() => {
    const lastMessage = messages.at(-1);
    return lastMessage?.type === 'assistant' ? lastMessage.questions : undefined;
  }, [messages]);

  useEffect(() => {
    if (lastAssistantQuestions && lastAssistantQuestions.length > 0) {
      setSuggestionQueries(lastAssistantQuestions);
    }
  }, [lastAssistantQuestions]);

  // Memoized handlers
  const handleSendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      type: 'user',
      content: input,
      timestamp: new Date(),
    };

    const currentInput = input;
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await sendMessageApi({
        body: {
          message: currentInput,
          conversationId: currentSessionId || undefined,
        },
      });

      if (!res.data?.success) {
        throw new Error('Failed to send message');
      }

      const assistantMessage: ChatMessage = {
        id: res.data.data.sessionId,
        type: 'assistant',
        content: res.data.data.response,
        timestamp: new Date(),
        attachments: (res.data.data.attachments || []).map((att) => ({
          type: att.type,
          data: att.data || {},
        })),
        questions: res.data.data.questions || [],
      };

      setMessages((prev) => [...prev, assistantMessage]);

      if (res.data.data.sessionId) {
        setCurrentSessionId(res.data.data.sessionId);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        type: 'assistant',
        content: "I'm sorry, I encountered an error processing your request. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, currentSessionId, sendMessageApi]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setInput(suggestion);
  }, []);

  const handleClearConversation = useCallback(() => {
    setMessages([defaultAssistantMessage]);
    setCurrentSessionId(null);
    setSuggestionQueries(defaultSuggestionQueries);
  }, [defaultAssistantMessage, defaultSuggestionQueries]);

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  // Render closed state
  if (!isOpen) {
    return (
      <div className={`fixed bottom-6 right-6 z-50 ${className}`}>
        <div className="relative group">
          <Button
            onClick={() => setIsOpen(true)}
            className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-xl hover:shadow-2xl transition-all duration-300 border-0 hover:scale-105"
          >
            <BotMessageSquare className="h-6 w-6 text-white" />
          </Button>

          {/* Floating label */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-black/80 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none backdrop-blur-sm">
            Ask AI Assistant
            <div className="absolute top-full right-3 w-0 h-0 border-l-2 border-r-2 border-t-4 border-transparent border-t-black/80"></div>
          </div>
        </div>
      </div>
    );
  }

  // Determine widget classes
  const widgetClasses = isFullExpanded
    ? 'fixed inset-4 z-[100] shadow-2xl'
    : 'fixed bottom-6 right-6 z-50 w-96 shadow-2xl';

  let heightClasses = 'h-[600px]';
  if (isFullExpanded) {
    heightClasses = 'h-full';
  } else if (isMinimized) {
    heightClasses = 'h-14';
  }

  return (
    <div className={`${widgetClasses} ${className}`}>
      <Card
        className={`transition-all duration-300 ${heightClasses} flex flex-col bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50`}
      >
        {/* Header */}
        <CardHeader className="p-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-blue-50/50 to-purple-50/50 dark:from-blue-950/30 dark:to-purple-950/30 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">AI Assistant</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Smart IoT Analytics</p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                onClick={handleClearConversation}
                title="Clear conversation"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                onClick={() => setIsMinimized(!isMinimized)}
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                onClick={() => setIsFullExpanded(!isFullExpanded)}
                title={isFullExpanded ? 'Exit fullscreen' : 'Fullscreen'}
              >
                <Expand className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 hover:bg-red-100/50 dark:hover:bg-red-900/30"
                onClick={() => setIsOpen(false)}
                title="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Content */}
        {!isMinimized && (
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id || `${message.type}-${message.timestamp.getTime()}`}
                    message={message}
                    onCopy={copyToClipboard}
                  />
                ))}

                {isLoading && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Suggestions Panel */}
            <SuggestionPanel
              suggestions={suggestionQueries}
              onSuggestionClick={handleSuggestionClick}
              isCollapsed={isSuggestionsCollapsed}
              onToggleCollapse={() => setIsSuggestionsCollapsed(!isSuggestionsCollapsed)}
            />

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm">
              <div className="flex gap-3">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about trends, anomalies, or request visualizations..."
                  className="flex-1 bg-white/70 dark:bg-gray-800/70 border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm focus:bg-white dark:focus:bg-gray-800 transition-all duration-200"
                  disabled={isLoading}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading}
                  size="sm"
                  className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 shadow-lg hover:shadow-xl transition-all duration-200 px-4"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                AI-powered analytics with smart aggregation and context awareness
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
